/** Lightweight layout engines for the canvas. We don't pull dagre or
 *  elk; the graphs are small (anchor view = ~12 nodes, neighbourhood
 *  walk capped at 100), so a hand-rolled radial / hierarchical layout
 *  reads cleaner than a heavy dependency.
 *
 *  Two modes:
 *   - radial: pin the root at origin, place each shell on a circle
 *     of radius hop * R. Used for neighbourhood walks rooted at a
 *     specific node.
 *   - constellation: arrange nodes by type — people on the left,
 *     ideas on the right, etc. — for the no-root anchor view. */
import type { ApiEdge, ApiNode } from "./types";

export interface PositionedNode extends ApiNode {
  x: number;
  y: number;
}

const SHELL_RADIUS = 220;
const NODE_W = 180;
const NODE_H = 64;

/** Position nodes in concentric shells around `rootId`. Nodes that
 *  are unreachable from the root land on an outer ring at fixed
 *  spacing so the canvas never overlaps itself. */
export function radialLayout(
  nodes: ApiNode[],
  edges: ApiEdge[],
  rootId: string,
): PositionedNode[] {
  if (nodes.length === 0) return [];
  const byId = new Map<string, ApiNode>();
  for (const n of nodes) byId.set(n.id, n);
  const adj = new Map<string, Set<string>>();
  for (const n of nodes) adj.set(n.id, new Set());
  for (const e of edges) {
    if (!adj.has(e.from)) adj.set(e.from, new Set());
    if (!adj.has(e.to)) adj.set(e.to, new Set());
    adj.get(e.from)!.add(e.to);
    adj.get(e.to)!.add(e.from);
  }
  const hop = new Map<string, number>();
  const queue: string[] = [];
  if (byId.has(rootId)) {
    hop.set(rootId, 0);
    queue.push(rootId);
  }
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curHop = hop.get(cur) ?? 0;
    for (const nb of adj.get(cur) ?? []) {
      if (hop.has(nb)) continue;
      hop.set(nb, curHop + 1);
      queue.push(nb);
    }
  }
  // Group by hop. Unreached nodes go on a ring at hop = max+1.
  const reached = new Map<number, string[]>();
  let maxHop = 0;
  for (const n of nodes) {
    const h = hop.has(n.id) ? hop.get(n.id)! : -1;
    if (h > maxHop) maxHop = h;
  }
  const orphanHop = maxHop + 1;
  for (const n of nodes) {
    const h = hop.has(n.id) ? hop.get(n.id)! : orphanHop;
    if (!reached.has(h)) reached.set(h, []);
    reached.get(h)!.push(n.id);
  }
  const out: PositionedNode[] = [];
  for (const [h, ids] of reached) {
    if (h === 0) {
      const n = byId.get(ids[0])!;
      out.push({ ...n, x: 0, y: 0 });
      continue;
    }
    const r = h * SHELL_RADIUS;
    const N = ids.length;
    for (let i = 0; i < N; i++) {
      const theta = (2 * Math.PI * i) / N - Math.PI / 2;
      const n = byId.get(ids[i])!;
      out.push({
        ...n,
        x: Math.cos(theta) * r,
        y: Math.sin(theta) * r,
      });
    }
  }
  return out;
}

/** Group-by-type layout used when the canvas opens with no rooted
 *  walk. Buckets nodes by type and places each bucket on its own
 *  vertical column so the user can read "people" / "projects" /
 *  "ideas" at a glance. */
export function constellationLayout(nodes: ApiNode[]): PositionedNode[] {
  if (nodes.length === 0) return [];
  const buckets = new Map<string, ApiNode[]>();
  for (const n of nodes) {
    if (!buckets.has(n.type)) buckets.set(n.type, []);
    buckets.get(n.type)!.push(n);
  }
  const types = Array.from(buckets.keys()).sort();
  const colW = 260;
  const rowH = 110;
  const totalW = (types.length - 1) * colW;
  const out: PositionedNode[] = [];
  types.forEach((t, ci) => {
    const list = buckets.get(t) ?? [];
    const xCol = ci * colW - totalW / 2;
    list.forEach((n, ri) => {
      out.push({
        ...n,
        x: xCol,
        y: ri * rowH - ((list.length - 1) * rowH) / 2,
      });
    });
  });
  return out;
}

export const LAYOUT_NODE_WIDTH = NODE_W;
export const LAYOUT_NODE_HEIGHT = NODE_H;

/** Force-directed layout (Fruchterman-Reingold-ish). Cheap fixed-step
 *  simulation that runs ~100 iterations on the main thread; for our
 *  graph sizes (≤ 200 nodes by the time we virtualise) this finishes
 *  in well under a frame so we don't bother with a worker.
 *
 *  Edges pull connected nodes together; every pair of nodes pushes
 *  apart. The temperature `t` cools each iteration so the layout
 *  settles instead of oscillating. */
export function forceDirectedLayout(
  nodes: ApiNode[],
  edges: ApiEdge[],
): PositionedNode[] {
  const N = nodes.length;
  if (N === 0) return [];
  const W = Math.max(800, Math.sqrt(N) * 240);
  const H = Math.max(600, Math.sqrt(N) * 180);
  const k = Math.sqrt((W * H) / Math.max(N, 1));
  // Seed positions on a jittered grid so the simulation has something
  // to do; pure-random starts can drop two nodes onto the same
  // coordinate and stall the repulsion step.
  const cols = Math.ceil(Math.sqrt(N));
  const pos = nodes.map((n, i) => ({
    id: n.id,
    x: ((i % cols) - cols / 2) * (W / cols) + (Math.random() - 0.5) * 30,
    y: (Math.floor(i / cols) - cols / 2) * (H / cols) + (Math.random() - 0.5) * 30,
  }));
  const idx = new Map<string, number>();
  pos.forEach((p, i) => idx.set(p.id, i));

  let t = W / 10;
  const iters = 120;
  const cooling = 0.95;
  for (let step = 0; step < iters; step++) {
    const disp = pos.map(() => ({ x: 0, y: 0 }));
    // Repulsive force on every pair.
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = pos[i].x - pos[j].x;
        const dy = pos[i].y - pos[j].y;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const f = (k * k) / d;
        disp[i].x += (dx / d) * f;
        disp[i].y += (dy / d) * f;
        disp[j].x -= (dx / d) * f;
        disp[j].y -= (dy / d) * f;
      }
    }
    // Attractive force along every edge.
    for (const e of edges) {
      const i = idx.get(e.from);
      const j = idx.get(e.to);
      if (i == null || j == null) continue;
      const dx = pos[i].x - pos[j].x;
      const dy = pos[i].y - pos[j].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = (d * d) / k;
      disp[i].x -= (dx / d) * f;
      disp[i].y -= (dy / d) * f;
      disp[j].x += (dx / d) * f;
      disp[j].y += (dy / d) * f;
    }
    // Apply displacement clipped to current temperature.
    for (let i = 0; i < N; i++) {
      const dd = Math.sqrt(disp[i].x * disp[i].x + disp[i].y * disp[i].y) || 0.01;
      pos[i].x += (disp[i].x / dd) * Math.min(dd, t);
      pos[i].y += (disp[i].y / dd) * Math.min(dd, t);
    }
    t *= cooling;
  }

  return pos.map((p, i) => ({
    ...nodes[i],
    x: p.x,
    y: p.y,
  }));
}

export type LayoutMode = "hierarchical" | "force";
